"use client";

import { FlexColumn } from "@/components/Flex";
import { themeColors } from "@/utils/themeColors";
import { Editor as CoreEditor, Editor } from "@knowt/editor/core";
import { TableOfContentData, TableOfContentsStorage } from "@knowt/editor/extensions/tableOfContents";
import Collapse from "@mui/material/Collapse";
import { memo, useCallback, useState } from "react";
import { editorThemeColors } from "../../utils/editorThemeColors";
import { findNodeById } from "../../utils/findNodeById";
import TOCITemLine from "./components/TOCItemLine";
import TOCItemText from "./components/TOCItemText";
import { useEditorState } from "@knowt/editor/react";
import useDOMEventListener from "@/hooks/useDOMEventListener";

const ShrinkTableOfContents = ({
    ref,
    content,
    style,
    onPointerEnter,
}: {
    content: TableOfContentData;
    style?: React.CSSProperties;
    onPointerEnter?: () => void;
    ref?: React.RefObject<HTMLDivElement>;
}) => {
    return (
        <FlexColumn
            ref={ref}
            className="scrollbar"
            style={{
                gap: "1.5rem",
                width: "3rem",
                height: "22rem",
                overflow: "hidden",
                zIndex: 2,
                ...style,
            }}
            onPointerEnter={onPointerEnter}>
            {content.map(item => {
                return (
                    <TOCITemLine
                        key={item.id}
                        isActive={item.isActive}
                        style={{
                            backgroundColor: item.isActive ? editorThemeColors.neutral3 : editorThemeColors.neutral1,
                            width: item.level === 1 ? "100%" : item.level === 2 ? "50%" : "25%",
                        }}
                    />
                );
            })}
        </FlexColumn>
    );
};

const ExpandTableOfContents = ({
    ref,
    content,
    style,
    onPointerLeave,
    editor,
}: {
    editor: Editor;
    content: TableOfContentData;
    style?: React.CSSProperties;
    onPointerLeave?: () => void;
    ref?: React.RefObject<HTMLDivElement>;
}) => {
    // biome-ignore lint/correctness/useExhaustiveDependencies: we do need `content` in deps
    const scrollToHeadingId = useCallback(
        (id: string) => {
            const element = document.getElementById(id);
            if (!element) return;

            const headingNode = findNodeById(editor, id);

            if (headingNode) {
                editor.commands.setTextSelection(headingNode.end);
                const offset = 130; // note header height + toolbar, make sure to scroll below this
                const scrollPos = element.getBoundingClientRect().top + window.scrollY - offset;
                window.scrollTo({ top: scrollPos, behavior: "smooth" });
            }
        },
        [content, editor]
    );

    return (
        <FlexColumn
            ref={ref}
            onPointerLeave={onPointerLeave}
            style={{
                borderRadius: "0.4rem",
                border: `1px solid ${themeColors.neutral1}`,
                backgroundColor: themeColors.neutralWhite,
                boxShadow: "0px 0px 7px 0px rgba(0, 0, 0, 0.10)",
                padding: "1.6rem",
                gap: "0.2rem",
                minWidth: "20rem",
                maxWidth: "23rem",
                zIndex: 2,
                maxHeight: "45rem",
                overflow: "hidden auto",
                ...style,
            }}>
            {content.map(item => {
                return (
                    <TOCItemText
                        className={item.isActive ? "secondaryTextBold1" : "secondaryText1"}
                        key={item.id}
                        level={item.level}
                        onClick={() => scrollToHeadingId(item.id)}
                        style={{
                            color: (() => {
                                if (item.isActive) {
                                    return editorThemeColors.neutral3;
                                }
                                return editorThemeColors.neutral2;
                            })(),
                        }}>
                        {item.textContent}
                    </TOCItemText>
                );
            })}
        </FlexColumn>
    );
};

export const TableOfContentsUI = memo(({ editor, style }: { editor: CoreEditor; style?: React.CSSProperties }) => {
    const [shouldExpand, setShouldExpand] = useState(false);
    const [_, setRerender] = useState(0);

    useDOMEventListener({
        on: "scrollend",
        handler: () => {
            setRerender(Math.random());
        },
    });

    const content = useEditorState({
        editor,
        selector: ctx => {
            if (!ctx.editor) return [];
            return (ctx.editor.storage.tableOfContents as TableOfContentsStorage).content;
        },
        equalityFn: (a, b) => a === b,
    });

    if (!content.length || !editor) {
        return null;
    }

    return (
        <>
            <Collapse in={!shouldExpand} orientation="horizontal" style={style} timeout={120}>
                <ShrinkTableOfContents content={content} onPointerEnter={() => setShouldExpand(true)} />
            </Collapse>
            <Collapse in={shouldExpand} orientation="horizontal" style={style} timeout={120}>
                <ExpandTableOfContents
                    editor={editor}
                    content={content}
                    onPointerLeave={() => setShouldExpand(false)}
                />
            </Collapse>
        </>
    );
});

TableOfContentsUI.displayName = "TableOfContentsUI";
